import { z } from "zod";

import { ERROR_TYPE } from "@/utils";

// Signal Priority Level enum - matches backend SignalPriorityLevel
export enum SignalPriorityLevel {
	PRIORITY_1 = "1",
	PRIORITY_2 = "2",
	PRIORITY_3 = "3",
	DISQUALIFIED = "disqualified",
}

export type PriorityLevelValue = SignalPriorityLevel;

// Priority item schema - multiple items per priority level
export const SignalPriorityItemSchema = z.object({
	id: z.number().optional(),
	listSignalConfigId: z.number().optional(),
	priorityLevel: z.nativeEnum(SignalPriorityLevel),
	title: z.string().min(1, ERROR_TYPE.REQUIRED),
	definition: z.string().min(1, ERROR_TYPE.REQUIRED),
	examples: z.array(z.string()).min(1, "At least one example is required"),
	order: z.number().default(0),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export type SignalPriorityItem = z.infer<typeof SignalPriorityItemSchema>;

// Main ListSignalConfig schema - 1:1 relationship with List
export const ListSignalConfigSchema = z.object({
	id: z.number().nullable().optional(),
	listId: z.number(),

	// AI Prompts
	deepResearchPrompt: z.string().default(""),
	systemPrompt: z.string().default(""),

	// Signal categorization prompts
	leadUpdatesPrompt: z.string().default(""),
	companyUpdatesPrompt: z.string().default(""),
	industryUpdatesPrompt: z.string().default(""),

	// Research options
	checkLinkedinPosts: z.boolean().default(false),
	analyzeLinkedinEngagement: z.boolean().default(false),
	checkTechStackChanges: z.boolean().default(false),

	// AI output language (ISO code); null = English
	language: z.string().nullable().optional(),

	// Priority definitions
	priorities: z.array(SignalPriorityItemSchema).default([]),

	createdAt: z.string().nullable().optional(),
	updatedAt: z.string().nullable().optional(),
});

export type ListSignalConfig = z.infer<typeof ListSignalConfigSchema>;

// Schema for creating/updating signal config (request body)
export const UpdateSignalConfigSchema = z.object({
	deepResearchPrompt: z.string().optional(),
	systemPrompt: z.string().optional(),
	leadUpdatesPrompt: z.string().optional(),
	companyUpdatesPrompt: z.string().optional(),
	industryUpdatesPrompt: z.string().optional(),
	checkLinkedinPosts: z.boolean().optional(),
	analyzeLinkedinEngagement: z.boolean().optional(),
	checkTechStackChanges: z.boolean().optional(),
	language: z.string().nullable().optional(),
	priorities: z
		.array(
			z.object({
				id: z.number().optional(),
				priorityLevel: z.nativeEnum(SignalPriorityLevel),
				title: z.string().min(1, ERROR_TYPE.REQUIRED),
				definition: z.string().min(1, ERROR_TYPE.REQUIRED),
				examples: z.array(z.string()).min(1, "At least one example is required"),
				order: z.number(),
			})
		)
		.optional(),
});

export type UpdateSignalConfig = z.infer<typeof UpdateSignalConfigSchema>;

// Schema for creating/updating priority item (used in modal)
export const CreateSignalPriorityItemSchema = z.object({
	priorityLevel: z.nativeEnum(SignalPriorityLevel),
	title: z.string().min(1, ERROR_TYPE.REQUIRED),
	definition: z.string().min(1, ERROR_TYPE.REQUIRED),
	examples: z.array(z.string()).min(1, "At least one example is required"),
	order: z.number(),
});

export type CreateSignalPriorityItem = z.infer<typeof CreateSignalPriorityItemSchema>;
